/**
 * Espelho de `backend/src/services/dataDoCartao.ts`.
 *
 * ── Por que existe uma segunda cópia ──────────────────────────────────────
 *
 * O backend decide as datas quando o método já é conhecido — veio da frase ou
 * do histórico. Mas no campo único, quando o método é uma LACUNA, quem
 * escolhe é ela, no navegador, depois da resposta. Nesse instante o pagamento
 * precisa ser recalculado sem uma ida ao servidor.
 *
 * O mesmo vale para o formulário completo.
 *
 * ── O que impede as duas de divergirem ────────────────────────────────────
 *
 * `shared/dataDoCartao.casos.json`. As duas suítes leem AQUELE arquivo, então
 * acrescentar um caso obriga os dois lados a passarem. É a lição de
 * `monthView` e do formatador de mês, onde a segunda cópia divergiu em
 * silêncio até alguém ver na tela.
 *
 * A regra, em uma linha (desde 13/09): a data escrita é a da COMPRA, e com dia
 * de fatura o pagamento vai para esse dia no mês seguinte à compra — ou, com o
 * dia de fechamento preenchido (15/09), para a fatura em que a compra entra de
 * fato. O porquê inteiro está no backend.
 */

const pad = (n: number) => String(n).padStart(2, '0');

const bissexto = (ano: number) => (ano % 4 === 0 && ano % 100 !== 0) || ano % 400 === 0;

const DIAS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export const diasNoMes = (ano: number, mes: number): number =>
  mes === 2 && bissexto(ano) ? 29 : DIAS[mes - 1];

/**
 * O dia de pagamento da fatura em que a compra entra. Âncora na compra.
 *
 * Sem `closingDay`: o mês seguinte à compra. Com ele (15/09): compra antes do
 * fechamento entra na fatura deste mês, no dia do fechamento ou depois na do
 * próximo; e a fatura vence no mesmo mês do fechamento quando o vencimento é
 * depois dele. O porquê inteiro está no backend.
 */
export const proximoPagamento = (
  dataDaCompra: string,
  billingDay: number,
  closingDay?: number | null,
): string => {
  const [ano, mes, diaDaCompra] = dataDaCompra.split('-').map(Number);
  let meses = 1;
  if (diaValido(closingDay)) {
    const fecha = Math.min(Math.trunc(closingDay), diasNoMes(ano, mes));
    const faturaDoMesQueVem = diaDaCompra >= fecha ? 1 : 0;
    const venceNoMesSeguinteAoFechamento = Math.trunc(billingDay) > Math.trunc(closingDay) ? 0 : 1;
    meses = faturaDoMesQueVem + venceNoMesSeguinteAoFechamento;
  }
  const abs = ano * 12 + (mes - 1) + meses;
  const proxAno = Math.floor(abs / 12);
  const proxMes = (abs % 12) + 1;
  const dia = Math.min(Math.max(Math.trunc(billingDay), 1), diasNoMes(proxAno, proxMes));
  return `${proxAno}-${pad(proxMes)}-${pad(dia)}`;
};

export type OrigemDaCompra = 'informada' | 'hoje';
export type OrigemDoPagamento = 'compra' | 'cartao';

export interface DatasDecididas {
  compra: string;
  pagamento: string;
  origemDaCompra: OrigemDaCompra;
  origemDoPagamento: OrigemDoPagamento;
  diaDoCartao: number | null;
}

const diaValido = (dia: number | null | undefined): dia is number =>
  typeof dia === 'number' && Number.isFinite(dia) && dia >= 1 && dia <= 31;

export const decidirDatas = (p: {
  /** O que ela escreveu — é a data da COMPRA. */
  dataInformada: string | null | undefined;
  billingDay: number | null | undefined;
  closingDay?: number | null;
  hojeISO: string;
}): DatasDecididas => {
  const compra = p.dataInformada || p.hojeISO;
  const origemDaCompra: OrigemDaCompra = p.dataInformada ? 'informada' : 'hoje';

  if (diaValido(p.billingDay)) {
    return {
      compra,
      pagamento: proximoPagamento(compra, p.billingDay, p.closingDay),
      origemDaCompra,
      origemDoPagamento: 'cartao',
      diaDoCartao: Math.trunc(p.billingDay),
    };
  }
  return { compra, pagamento: compra, origemDaCompra, origemDoPagamento: 'compra', diaDoCartao: null };
};

/**
 * Decide de novo com o método escolhido agora — realimentando a COMPRA.
 *
 * Realimentar o pagamento somaria um mês a cada volta. Ver a nota longa no
 * backend; aqui o risco é o mesmo, em `QuickEntry.escolher()`.
 */
export const redecidirDatas = (
  anterior: Pick<DatasDecididas, 'compra' | 'origemDaCompra'>,
  billingDay: number | null | undefined,
  closingDay?: number | null,
): DatasDecididas => {
  const r = decidirDatas({ dataInformada: anterior.compra, billingDay, closingDay, hojeISO: anterior.compra });
  return { ...r, origemDaCompra: anterior.origemDaCompra };
};

/** A data da compra de uma transação gravada. `NULL` no banco = a compra é o pagamento. */
export const dataDaCompraDe = (t: { purchaseDate?: string | null; date: string }): string =>
  t.purchaseDate || t.date;

/**
 * As duas datas quando o método muda — cópia de `datasAoTrocarMetodo` do
 * backend, presa a ele por `trocaDeMetodo` na tabela compartilhada.
 *
 * Âncora na compra: trocar duas vezes para o mesmo cartão não anda o
 * pagamento. `purchaseDate` null = a compra é o pagamento.
 */
export const datasAoTrocarMetodo = (
  t: { date: string; purchaseDate?: string | null },
  billingDay: number | null | undefined,
  closingDay?: number | null,
): { date: string; purchaseDate: string | null } => {
  const compra = dataDaCompraDe(t).slice(0, 10);
  const { pagamento } = redecidirDatas({ compra, origemDaCompra: 'informada' }, billingDay, closingDay);
  return { date: pagamento, purchaseDate: compra !== pagamento ? compra : null };
};

/**
 * "Hoje" no navegador.
 *
 * Aqui é o relógio DELA, não o do servidor — o motivo da armadilha 3 (servidor
 * em UTC virando o dia às 21h BRT) não se aplica ao navegador, que já está no
 * fuso certo.
 */
export const hojeISOLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
